/**
 * Share-accept boot UI (?share=<id>.<token>):
 *  - ShareConfirmDialog — the device already has an order with prints; ask
 *    before replacing it (Save my order… / Start new order / Cancel).
 *  - ShareFailedScreen — expired/invalid link (or a network hiccup) with a
 *    way forward.
 *  - ShareBanner — "N photos were shared with you" toast on Step 2.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { shareBannerText } from '../../lib/share';
import { useStore, type ShareFlow } from '../../store/store';
import { CartModal } from '../cart/CartModal';
import { SaveOrderModal } from '../cart/SaveOrderModal';
import '../cart/cart.css';
import './share.css';

export function ShareConfirmDialog({ param }: { param: string }) {
  const acceptShare = useStore((s) => s.acceptShare);
  const dismiss = useStore((s) => s.dismissShareConfirm);
  const [saveOpen, setSaveOpen] = useState(false);

  if (saveOpen) return <SaveOrderModal onClose={() => setSaveOpen(false)} />;

  return (
    <CartModal title="Photos were shared with you" onClose={dismiss}>
      <div className="sharegate">
        <p className="sharegate__body">
          Start a new order from these shared photos? The order you were working on stays saved on
          this device until you replace it — use <strong>Save my order</strong> first if you want a
          backup link.
        </p>
        <div className="cmodal__actions sharegate__actions">
          <button type="button" className="btn btn--secondary" onClick={() => setSaveOpen(true)}>
            Save my order…
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void acceptShare(param)}
          >
            Start new order
          </button>
          <button type="button" className="btn btn--secondary" onClick={dismiss}>
            Cancel
          </button>
        </div>
      </div>
    </CartModal>
  );
}

/** Minimal branded shell (mirrors SystemScreens' layout). */
function ShareShell({ children }: { children: ReactNode }) {
  return (
    <div className="app">
      <header className="hdr">
        <div className="hdr__brand">
          <span className="hdr__wordmark">REWIND</span>
          <span className="hdr__eyebrow">Print Order</span>
        </div>
        <div className="hdr__right" />
      </header>
      <main className="app__main">
        <div className="center-screen">{children}</div>
      </main>
    </div>
  );
}

export function ShareFailedScreen({ flow }: { flow: Extract<ShareFlow, { state: 'failed' }> }) {
  const acceptShare = useStore((s) => s.acceptShare);
  const exitShare = useStore((s) => s.exitShare);

  if (flow.error === 'network') {
    return (
      <ShareShell>
        <h1 className="center-screen__title">Something went wrong</h1>
        <p className="center-screen__body">
          We couldn&rsquo;t open the shared photos. Check your connection and try again.
        </p>
        <div className="sharegate__row">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void acceptShare(flow.param)}
          >
            Try again
          </button>
          <button type="button" className="btn btn--secondary" onClick={() => void exitShare()}>
            Start a fresh order
          </button>
        </div>
      </ShareShell>
    );
  }

  const expired = flow.error === 'share_expired';
  return (
    <ShareShell>
      <h1 className="center-screen__title">
        {expired ? 'This share link has expired' : 'This share link isn’t working'}
      </h1>
      <p className="center-screen__body">
        {expired
          ? 'Ask your friend to send a new one — share links work for 30 days.'
          : 'The link may be incomplete or out of date. Ask your friend to send a new one.'}
      </p>
      <button type="button" className="btn btn--primary" onClick={() => void exitShare()}>
        Start a fresh order
      </button>
    </ShareShell>
  );
}

export function ShareBanner() {
  const count = useStore((s) => s.shareBanner);
  const dismiss = useStore((s) => s.dismissShareBanner);
  if (count == null) return null;
  return (
    <div className="share-banner" role="status">
      <span>{shareBannerText(count)}</span>
      <button type="button" className="share-banner__x" onClick={dismiss} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
}
